import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  GlossaryHint,
  Skeleton,
  StatusBadge,
  SubmitButton,
  useSubmittingMutation,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatRelativeTime } from "@/design-system/lib/format";
import { PortalApiError } from "../api/client";
import { brokerageStripeApi, type BrokerageConnectStatus } from "../api/brokerageStripe";

// Brokerage / Sub-Broker PR-6b — the brokerage firm's self-serve "Payment setup"
// tab. The firm sees its single Stripe Connect PAYOUT account's live status
// (receives what Phoenix pays the brokerage) and can generate a FRESH
// Stripe-hosted onboarding link on demand — robust against a one-time emailed
// link expiring. Stripe's hosted flow leaves the app (window.location) and
// returns to ?connect=return. Single-account simplification of TpsPaymentSetupTab.
// No React Query in the portal — the read is plain useState with a cancelled
// guard, writes go through useSubmittingMutation.

// On return from Stripe hosted onboarding (?connect=return), poll the live status
// a few times with backoff so the tab advances to "Ready" without a manual click.
// getStatus() live-fetches Stripe (1 Accounts.Get per call), so this is bounded at
// 4 attempts and stops as soon as the account reports Active. The manual "Refresh
// status" button stays as the fallback. ?connect=refresh (an Account Link that
// expired mid-onboarding) is deliberately NOT handled here.
const RETURN_POLL_BACKOFF_MS = [2000, 3000, 5000, 8000];

interface AccountUi {
  label: string;
  tone: StatusTone;
  hint: string;
  /** The firm can start/continue onboarding (account isn't Active yet). */
  actionable: boolean;
  buttonLabel: string;
}

// Maps the backend Connect status → plain language. "NotStarted" = no account
// row yet (the firm hasn't begun); the onboarding-link call provisions on first use.
function accountUi(status: string): AccountUi {
  switch (status) {
    case "Active":
      return {
        label: "Ready",
        tone: "ok",
        hint: "Verified with Stripe and ready to receive payouts.",
        actionable: false,
        buttonLabel: "Ready ✓",
      };
    case "Restricted":
      return {
        label: "Verifying with Stripe",
        tone: "warn",
        hint: "Stripe still needs a little more information before your account is ready.",
        actionable: true,
        buttonLabel: "Continue setup",
      };
    case "Disabled":
      return {
        label: "Action needed",
        tone: "danger",
        hint: "Stripe has paused your account. Continue setup to resolve what it needs.",
        actionable: true,
        buttonLabel: "Continue setup",
      };
    case "Pending":
      return {
        label: "Setup in progress",
        tone: "warn",
        hint: "You've started setup but Stripe hasn't verified your details yet.",
        actionable: true,
        buttonLabel: "Continue setup",
      };
    case "NotStarted":
    default:
      return {
        label: "Not started",
        tone: "warn",
        hint: "Setup hasn't been started yet. It takes just a few minutes.",
        actionable: true,
        buttonLabel: "Set up payout account",
      };
  }
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

export function BrokeragePaymentSetupTab() {
  const toast = useToast();
  const [status, setStatus] = useState<BrokerageConnectStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [params, setParams] = useSearchParams();
  // Capture the return marker ONCE (lazy init) so cleaning it from the URL below
  // doesn't drop the "welcome back" note on re-render.
  const [returnedFromStripe] = useState(() => params.get("connect") === "return");
  // Latest completion flag, read inside the auto-poll loop without re-subscribing.
  const completeRef = useRef(false);

  // Consume ?connect so a refresh/re-render can't re-trigger the note. One-shot.
  useEffect(() => {
    if (params.get("connect")) {
      const next = new URLSearchParams(params);
      next.delete("connect");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load (and re-load, on retry) the live status.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await brokerageStripeApi.getStatus();
        if (!cancelled) {
          setStatus(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load your payment setup."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadNonce]);

  // Keep the completion flag current for the auto-poll loop.
  useEffect(() => {
    completeRef.current = status?.onboardingComplete ?? false;
  }, [status]);

  // On return from Stripe, poll the live status a few times with backoff so the
  // tab advances to "Ready" without a manual click. Stops as soon as the account
  // is Active (or after the bounded attempts).
  useEffect(() => {
    if (!returnedFromStripe) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = (i: number) => {
      if (cancelled || i >= RETURN_POLL_BACKOFF_MS.length) return;
      timer = setTimeout(async () => {
        if (cancelled || completeRef.current) return;
        try {
          const data = await brokerageStripeApi.getStatus();
          if (cancelled) return;
          setStatus(data);
          if (data.onboardingComplete) return; // done — stop polling
        } catch {
          // A transient fetch failure shouldn't spin the loop; fall through to the
          // next backoff step. The manual "Refresh status" button remains.
        }
        attempt(i + 1);
      }, RETURN_POLL_BACKOFF_MS[i]);
    };
    attempt(0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [returnedFromStripe]);

  const refresh = useSubmittingMutation(
    async () => {
      const data = await brokerageStripeApi.getStatus();
      setStatus(data);
    },
    {
      onSuccess: () => toast.success("Status updated."),
      onError: (err) => toast.error(errorMessage(err, "Couldn't refresh your status.")),
    },
  );

  // Begin (or resume) Stripe hosted onboarding — the browser leaves the app for
  // Stripe's secure flow (mirrors PayeePaymentSetupPanel / TpsPaymentSetupTab).
  const start = useSubmittingMutation(
    async () => {
      const result = await brokerageStripeApi.onboardingLink();
      if (!result.onboardingUrl) {
        throw new Error("Stripe didn't return a setup link. Please refresh and try again.");
      }
      window.location.href = result.onboardingUrl;
    },
    {
      onError: (err) =>
        toast.error(errorMessage(err, "We couldn't open the setup page. Please try again.")),
    },
  );

  if (error && !status) {
    return (
      <Card data-testid="brokerage-payment-setup-error">
        <CardContent className="space-y-space-3">
          <p className="text-small text-status-error m-0" role="alert">
            {error}
          </p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setReloadNonce((n) => n + 1)}
            data-testid="brokerage-payment-setup-retry"
          >
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!status) {
    return <Skeleton className="h-64 w-full" data-testid="brokerage-payment-setup-loading" />;
  }

  const ui = accountUi(status.status);
  const isActive = status.status === "Active";

  return (
    <div className="space-y-space-4" data-testid="brokerage-payment-setup">
      {returnedFromStripe && !status.onboardingComplete && (
        <div
          role="status"
          className="rounded-lg border border-status-info bg-status-info/10 p-space-3"
          data-testid="brokerage-payment-setup-return"
        >
          <p className="text-small font-semibold text-onyx m-0">
            Welcome back — we&apos;re checking Stripe&apos;s verification status.
          </p>
          <p className="mt-space-1 text-caption text-gray-mid m-0">
            What&apos;s next: Stripe can take a few minutes to verify. If your account still shows
            &ldquo;Verifying,&rdquo; use <span className="font-semibold">Refresh status</span> in a
            moment.
          </p>
        </div>
      )}

      {status.onboardingComplete ? (
        <Card data-testid="brokerage-payment-setup-complete">
          <CardContent className="space-y-space-2">
            <div className="flex items-center gap-space-2">
              <CheckCircle2 size={20} strokeWidth={1.5} className="text-status-success" aria-hidden />
              <StatusBadge tone="ok">Payout account ready</StatusBadge>
            </div>
            <p className="text-small text-onyx m-0">
              You&apos;re all set. Phoenix can pay your brokerage to this account
              {status.onboardedAt && <> — verified {formatRelativeTime(status.onboardedAt)}</>}.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="brokerage-payment-setup-account" className="border-gold">
          <CardContent className="flex flex-wrap items-start justify-between gap-space-3">
            <div className="min-w-0 space-y-space-1">
              <div className="flex flex-wrap items-center gap-space-2">
                <h3 className="text-h4 font-semibold text-onyx m-0">Your payout account</h3>
                <StatusBadge tone={ui.tone} hint={ui.hint}>
                  {ui.label}
                </StatusBadge>
              </div>
              <p className="text-small text-gray-mid m-0">
                Receives the payments Phoenix makes to your brokerage.
              </p>
            </div>
            <div className="shrink-0">
              <Button
                type="button"
                variant="primary"
                size="sm"
                loading={start.pending}
                disabled={start.pending}
                onClick={() => void start.submit()}
                data-testid="brokerage-payment-setup-action"
              >
                {ui.buttonLabel}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Why this exists. */}
      <Card data-testid="brokerage-payment-setup-explainer">
        <CardContent className="space-y-space-3">
          <div className="space-y-space-1">
            <h3 className="text-h4 font-semibold text-onyx m-0">Your payout account</h3>
            <p className="text-small text-gray-mid m-0">
              Phoenix pays your brokerage through a secure Stripe account. Setting it up lets Phoenix
              route money to you automatically — Stripe collects and verifies your business and bank
              details so Phoenix never has to store them.
            </p>
          </div>
          <div className="rounded-lg border border-border-light bg-white-warm p-space-3">
            <p className="text-caption font-semibold uppercase tracking-wide text-gray-mid m-0">
              What each status means
            </p>
            <dl className="mt-space-2 grid grid-cols-1 gap-space-1 text-caption text-onyx sm:grid-cols-2">
              <StatusMeaning label="Not started" meaning="Setup hasn't begun." />
              <StatusMeaning label="Verifying with Stripe" meaning="Stripe is reviewing your details." />
              <StatusMeaning label="Ready" meaning="Verified and able to receive payouts." />
              <StatusMeaning label="Action needed" meaning="Stripe needs you to resolve something." />
            </dl>
          </div>
        </CardContent>
      </Card>

      {!isActive && (
        <div className="flex flex-wrap items-center gap-space-2">
          <SubmitButton
            type="button"
            variant="secondary"
            size="sm"
            pending={refresh.pending}
            pendingLabel="Checking…"
            onClick={() => void refresh.submit()}
            data-testid="brokerage-payment-setup-refresh"
          >
            Refresh status
          </SubmitButton>
          <p className="text-caption text-gray-mid m-0">
            Just finished on Stripe? Refresh to pull your latest status.
          </p>
        </div>
      )}

      <PrepCard />
    </div>
  );
}

function StatusMeaning({ label, meaning }: { label: string; meaning: string }) {
  return (
    <div>
      <dt className="inline font-semibold text-onyx">{label}:</dt>{" "}
      <dd className="inline m-0 text-gray-mid">{meaning}</dd>
    </div>
  );
}

// Static "what you'll need" prep card.
function PrepCard() {
  return (
    <Card data-testid="brokerage-payment-setup-prep">
      <CardContent className="space-y-space-3">
        <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid m-0">
          What you&apos;ll need
        </h3>
        <p className="text-small text-onyx m-0">
          Have these handy before you start — Stripe asks for them to verify your firm:
        </p>
        <ul className="list-disc space-y-space-1 pl-space-5 m-0 text-small text-onyx">
          <li>Your legal business name (exactly as registered).</li>
          <li>
            Your <span className="font-semibold">EIN</span> (Employer Identification Number — your
            business tax ID).
          </li>
          <li>Your business address.</li>
          <li>
            Your bank&apos;s <GlossaryHint term="routing number">routing number</GlossaryHint> and{" "}
            <GlossaryHint term="account number">account number</GlossaryHint> for the account that
            should receive payouts.
          </li>
        </ul>
        <p className="rounded-lg border border-border-light bg-white-warm p-space-3 text-caption text-gray-mid m-0">
          Your details are collected and verified securely by Stripe, our payout partner. Phoenix
          never stores your full bank account number.
        </p>
      </CardContent>
    </Card>
  );
}
